/**
 * Training drills: a fixed shot, a clock, and a tally.
 *
 * NOT the same thing as `Training` in the menu, which is the reinforcement
 * learning screen (src/ui/training.ts) and trains a BOT. This trains the
 * player. The two share a word and nothing else, which is why the menu calls
 * this one Practice.
 *
 * ===========================================================================
 * WHY THIS EXISTS
 * ===========================================================================
 *
 * Everything that separates a player who has been here an hour from one who
 * has been here a week — hitting a ball that is above the roof of the car,
 * reading a bounce off the side wall, arriving at the ball before it lands —
 * happens perhaps twice in a five-minute match, and only if the ball happens
 * to go there. Free play is the usual answer and it is a bad one: the ball is
 * wherever the last shot left it, so the state a player most needs to practise
 * is the one they are least likely to get.
 *
 * A drill is that state, on demand, ten times in a row.
 *
 * ===========================================================================
 * WHAT IS IN THIS FILE AND WHAT IS NOT
 * ===========================================================================
 *
 * The catalogue and the run's state machine are here, and both are pure. A
 * `DrillRun` never sees a `World`: it is told whether the ball went in and how
 * many ticks have passed, and it answers with what should happen next. That is
 * what lets every rule below — when an attempt ends, what counts as a miss,
 * when the session is over — be tested without a simulation.
 *
 * `applyDrillShot` is the one function that touches simulation state, and it
 * writes only what `resetCarToSpawn` and the ball's own fields already are. It
 * is here rather than in src/sim because a drill placement is GAME content, in
 * the same sense that ./bots and ./challenges are: nothing in the simulation
 * knows a drill exists.
 *
 * ===========================================================================
 * THE COORDINATE FRAME, BECAUSE EVERY PLACEMENT BELOW IS WRITTEN IN IT
 * ===========================================================================
 *
 *   +Y   the ORANGE goal, at y = 5120. The player is on blue and shoots this
 *        way in every drill — a drill that switched ends would be teaching the
 *        player to re-learn which way is forward.
 *   +X   right, from the player's kickoff view. Side walls at +/-4096.
 *   +Z   up. Floor 0, ceiling 2048, ball resting height 91.25.
 *
 * See src/physics/arena-data.ts, which owns all of those numbers.
 */

import { BALL_RADIUS, CAR_SPAWN_REST_Z } from '../physics/constants';
import type { BallState } from '../physics/ball';
import type { CarState } from '../physics/car-state';
import { clone, vec3, type Vec3 } from '../physics/math';
import { resetCarToSpawn } from '../sim/kickoff';
import { DEFAULT_MATCH_CONFIG, type MatchConfig } from './match';
import { UNLIMITED_MATCH_DURATION } from './settings';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** Where one attempt starts. Everything not named is at rest. */
export interface DrillShot {
  /** Ball centre. `z` is measured from the floor, so a rolling ball is 91.25. */
  readonly ball: Vec3;
  /** Ball velocity, uu/s. Absent means a ball sitting still. */
  readonly ballVelocity?: Vec3;
  /** Car centre. `z` is filled in with the resting height; only x and y matter. */
  readonly car: { readonly x: number; readonly y: number };
  /** Car heading, radians. 0 faces +X; `Math.PI / 2` faces the orange goal. */
  readonly yaw: number;
  /** Tank at the start of the attempt, 0..100. */
  readonly boost: number;
}

/**
 * What ends an attempt successfully.
 *
 * `score` is the honest test for a shooting drill and a bad one for a control
 * drill: a player learning to touch a ball at the top of its arc has done the
 * thing being taught whether or not the resulting shot was on target, and a
 * drill that says MISSED to a clean aerial touch is teaching the wrong lesson.
 */
export type DrillGoal = 'score' | 'touch';

export interface DrillSpec {
  readonly id: string;
  readonly name: string;
  /** One line, on the row. What the drill puts in front of you. */
  readonly blurb: string;
  /** The note under the focused row. What it is for. */
  readonly teaches: string;
  readonly goal: DrillGoal;
  /** Seconds an attempt gets before it is a miss. */
  readonly limitSeconds: number;
  /**
   * The shots, in order, cycled if the session is longer than the list. Fixed
   * rather than random: a player repeating a drill is trying to feel the same
   * ball twice, and a fresh random shot every attempt makes that impossible.
   */
  readonly shots: readonly DrillShot[];
}

/** Attempts in one session, whatever the shot list's length. */
export const DRILL_ATTEMPTS = 10;

/** Seconds between an attempt ending and the next ball appearing. */
export const DRILL_RESET_SECONDS = 1;

/**
 * The match configuration a drill session runs under.
 *
 * EVERY PHASE DURATION IS ZERO, and that is the load-bearing part rather than
 * a preference. A frozen phase in `MatchState` is one where `World` re-asserts
 * a pose of its own — the kickoff pose during a countdown, the scoring pose
 * during a celebration (see `holdFrozenPose` in src/sim/world.ts) — and a
 * drill's whole job is to put the field somewhere the world would never put
 * it. Left non-zero, the countdown would overwrite each placed shot with a
 * kickoff, once per tick, and the ball would appear on the centre spot.
 *
 * So the world is given no frozen phases to hold, and the beat between
 * attempts is the run's own: `DRILL_RESET_SECONDS` of the next shot held in
 * place by the caller. A goal in a drill still passes through `goal` and
 * `replay`, but for one tick each.
 *
 * The clock is free play's, not a longer one of this file's own: `MatchState`
 * counts down whatever it is given and the HUD prints it, so a duration of a
 * hundred hours would read as "5999:00" in the corner. `UNLIMITED_MATCH_DURATION`
 * is the value already chosen for "there is no clock here" — 99:59, the
 * largest a two-digit minute field can show — and a drill is over in two
 * minutes either way.
 */
export const drillMatchConfig = (): MatchConfig => ({
  ...DEFAULT_MATCH_CONFIG,
  duration: UNLIMITED_MATCH_DURATION,
  countdownDuration: 0,
  goalCelebrationDuration: 0,
  replayDuration: 0,
  overtimeEnabled: false,
});

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

/** Facing the orange goal. Used by nearly every shot below. */
const UPFIELD = Math.PI / 2;

/**
 * The drills, in the order they are taught.
 *
 * Rolling first, because it is the only one a new player can already do and a
 * list whose first entry is impossible is a list nobody reaches the end of.
 * Then the two that are the actual skill ceiling — the aerial and the wall
 * read — then kickoffs, which every match opens with and almost nobody
 * practises.
 *
 * The numbers are authored, not sourced, and they were chosen by playing them:
 * a shot that cannot be reached in the time limit is not a drill, it is a
 * demonstration that the drill is broken.
 */
export const DRILLS: readonly DrillSpec[] = Object.freeze([
  {
    id: 'ground-shots',
    name: 'Ground Shots',
    blurb: 'A ball rolling across the box. Put it away.',
    teaches:
      'The angle you approach at is the angle it leaves at. Meet the ball on the side away from the net.',
    goal: 'score',
    limitSeconds: 8,
    shots: [
      { ball: vec3(-1500, 2600, BALL_RADIUS), ballVelocity: vec3(700, 0, 0), car: { x: -1800, y: 600 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(1500, 2600, BALL_RADIUS), ballVelocity: vec3(-700, 0, 0), car: { x: 1800, y: 600 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(0, 3200, BALL_RADIUS), ballVelocity: vec3(0, 500, 0), car: { x: -900, y: 900 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(-2400, 1800, BALL_RADIUS), ballVelocity: vec3(900, 600, 0), car: { x: 0, y: 0 }, yaw: UPFIELD, boost: 50 },
      { ball: vec3(2400, 1800, BALL_RADIUS), ballVelocity: vec3(-900, 600, 0), car: { x: 0, y: 0 }, yaw: UPFIELD, boost: 50 },
    ],
  },
  {
    id: 'power-shots',
    name: 'Power Shots',
    blurb: 'Halfway line, full tank, one touch to beat the keeper.',
    teaches:
      'Speed comes from the car, not the swing. Boost through the contact rather than up to it.',
    goal: 'score',
    limitSeconds: 9,
    shots: [
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: 0, y: -2200 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(-1200, -600, BALL_RADIUS), car: { x: -1200, y: -3000 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(1200, -600, BALL_RADIUS), car: { x: 1200, y: -3000 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(0, -1200, BALL_RADIUS), ballVelocity: vec3(0, 400, 0), car: { x: 0, y: -3600 }, yaw: UPFIELD, boost: 100 },
    ],
  },
  {
    id: 'aerials',
    name: 'Aerials',
    blurb: 'The ball is dropped in front of the net. Reach it before it lands.',
    teaches:
      'Jump, point the nose at where the ball WILL be, then boost. Aiming at where it is now is how you arrive underneath it.',
    goal: 'touch',
    limitSeconds: 7,
    shots: [
      { ball: vec3(0, 3000, 1250), ballVelocity: vec3(0, 0, 120), car: { x: 0, y: 700 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(-900, 2800, 1400), ballVelocity: vec3(120, 0, 0), car: { x: -900, y: 400 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(900, 2800, 1400), ballVelocity: vec3(-120, 0, 0), car: { x: 900, y: 400 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(0, 2200, 1700), ballVelocity: vec3(0, 260, -60), car: { x: 0, y: 0 }, yaw: UPFIELD, boost: 100 },
      { ball: vec3(-600, 3400, 1000), ballVelocity: vec3(0, 0, 320), car: { x: -1400, y: 900 }, yaw: UPFIELD, boost: 100 },
    ],
  },
  {
    id: 'wall-reads',
    name: 'Wall Reads',
    blurb: 'The ball comes off the side wall. Be where it lands.',
    teaches:
      'A wall bounce mirrors: the angle in is the angle out. Drive to the mirror point, not to the ball.',
    goal: 'touch',
    limitSeconds: 8,
    shots: [
      { ball: vec3(-3400, 1400, 400), ballVelocity: vec3(-900, 900, 250), car: { x: -600, y: 300 }, yaw: UPFIELD, boost: 60 },
      { ball: vec3(3400, 1400, 400), ballVelocity: vec3(900, 900, 250), car: { x: 600, y: 300 }, yaw: UPFIELD, boost: 60 },
      { ball: vec3(-3600, 2600, 900), ballVelocity: vec3(-700, 500, 400), car: { x: -1200, y: 1200 }, yaw: UPFIELD, boost: 60 },
      { ball: vec3(3600, 2600, 900), ballVelocity: vec3(700, 500, 400), car: { x: 1200, y: 1200 }, yaw: UPFIELD, boost: 60 },
    ],
  },
  {
    id: 'backboard',
    name: 'Backboard Reads',
    blurb: 'The ball comes off the wall above the net.',
    teaches:
      'It drops straight down the face of the backboard. Wait under it rather than chasing it up.',
    goal: 'score',
    limitSeconds: 9,
    shots: [
      { ball: vec3(0, 3600, 1100), ballVelocity: vec3(0, 1500, 500), car: { x: 0, y: 1600 }, yaw: UPFIELD, boost: 70 },
      { ball: vec3(-800, 3400, 900), ballVelocity: vec3(300, 1600, 600), car: { x: -700, y: 1300 }, yaw: UPFIELD, boost: 70 },
      { ball: vec3(800, 3400, 900), ballVelocity: vec3(-300, 1600, 600), car: { x: 700, y: 1300 }, yaw: UPFIELD, boost: 70 },
    ],
  },
  {
    id: 'kickoffs',
    name: 'Kickoffs',
    blurb: 'Every standard spawn, ball on the spot.',
    teaches:
      'The fast route is a diagonal boost into a flip, not a straight sprint. Arrive at speed or do not arrive.',
    goal: 'touch',
    limitSeconds: 5,
    shots: [
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: 0, y: -4608 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: -2048, y: -2560 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: 2048, y: -2560 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: -256, y: -3840 }, yaw: UPFIELD, boost: 34 },
      { ball: vec3(0, 0, BALL_RADIUS), car: { x: 256, y: -3840 }, yaw: UPFIELD, boost: 34 },
    ],
  },
]);

export const drillById = (id: string): DrillSpec | undefined =>
  DRILLS.find(drill => drill.id === id);

/** The shot an attempt gets. Cycles, so `DRILL_ATTEMPTS` is free of the list. */
export const shotFor = (spec: DrillSpec, attempt: number): DrillShot =>
  spec.shots[((attempt % spec.shots.length) + spec.shots.length) % spec.shots.length];

// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------

/** The two objects a shot writes into. Narrow so a test can pass literals. */
export interface DrillWorld {
  readonly ball: BallState;
  readonly cars: readonly CarState[];
}

/**
 * Put the world into a shot's starting state.
 *
 * The car goes through `resetCarToSpawn`, which is not merely convenient: it
 * is the ONE function that clears every piece of state a car carries between
 * lives — the jump machine, the powerslide ramp, the boost burst, the dodge
 * cooldown. A drill that placed a car by writing `position` alone would hand
 * the player attempt two with attempt one's flip already spent, and the bug
 * would look like the game randomly refusing to dodge.
 */
export const applyDrillShot = (world: DrillWorld, shot: DrillShot): void => {
  const car = world.cars[0];
  if (car !== undefined) {
    resetCarToSpawn(
      car,
      { position: vec3(shot.car.x, shot.car.y, CAR_SPAWN_REST_Z), yaw: shot.yaw, name: 'drill' },
      shot.boost
    );
  }

  const ball = world.ball;
  ball.position = clone(shot.ball);
  ball.velocity = shot.ballVelocity === undefined ? vec3(0, 0, 0) : clone(shot.ballVelocity);
  // Spin is deliberately zeroed rather than authored. A ball with spin curves,
  // and a drill whose whole point is "the bounce is predictable" must not open
  // with the one property that makes it not be.
  ball.angularVelocity = vec3(0, 0, 0);
};

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

/** What the last observed tick did to the run. */
export type DrillEvent =
  /** Nothing; the attempt continues. */
  | 'live'
  /** The attempt was met. The next shot is due. */
  | 'hit'
  /** The clock ran out. The next shot is due. */
  | 'missed'
  /** The last attempt has just resolved; `summary` is final. */
  | 'finished';

export interface DrillSummary {
  readonly drillId: string;
  readonly name: string;
  readonly hits: number;
  readonly attempts: number;
}

/**
 * One session of one drill.
 *
 * A state machine over ticks and nothing else — see the file header for why it
 * never sees a `World`. The caller feeds it what happened (`ballScored`,
 * `ballTouched`) and reads back what to do.
 *
 * ATTEMPTS ARE COUNTED WHEN THEY END, not when they start, so a run that is
 * abandoned mid-attempt reports the attempts the player actually finished.
 */
export class DrillRun {
  readonly spec: DrillSpec;
  /** 0-based index of the attempt in progress. */
  private index = 0;
  private hitCount = 0;
  private ticksLeft: number;
  private done = false;
  /** True between an attempt resolving and the next one being placed. */
  private resolved = false;

  constructor(spec: DrillSpec, private readonly ticksPerSecond: number) {
    this.spec = spec;
    this.ticksLeft = this.limitTicks();
  }

  private limitTicks(): number {
    return Math.max(1, Math.round(this.spec.limitSeconds * this.ticksPerSecond));
  }

  /** The shot the CURRENT attempt is played from. */
  shot(): DrillShot {
    return shotFor(this.spec, this.index);
  }

  /** 1-based, for display. Holds at the last attempt once the run is over. */
  get attempt(): number {
    return Math.min(this.index + 1, DRILL_ATTEMPTS);
  }

  get hits(): number {
    return this.hitCount;
  }

  get finished(): boolean {
    return this.done;
  }

  /** Seconds left in this attempt, for the HUD. Never negative. */
  get secondsLeft(): number {
    return Math.max(0, this.ticksLeft / this.ticksPerSecond);
  }

  /**
   * Fold in one tick.
   *
   * `ballScored` outranks the clock: a shot that crosses the line on the tick
   * the timer expires went in, and calling that a miss is the kind of unfair
   * a player remembers. A drill judged on `touch` ignores `ballScored`
   * entirely — it is already satisfied by the touch that produced it.
   */
  observe(events: { ballScored: boolean; ballTouched: boolean }): DrillEvent {
    if (this.done || this.resolved) return this.done ? 'finished' : 'live';

    const met = this.spec.goal === 'score' ? events.ballScored : events.ballTouched;
    if (met) return this.resolve(true);

    this.ticksLeft--;
    if (this.ticksLeft <= 0) return this.resolve(false);
    return 'live';
  }

  private resolve(hit: boolean): DrillEvent {
    if (hit) this.hitCount++;
    this.resolved = true;
    if (this.index + 1 >= DRILL_ATTEMPTS) {
      this.done = true;
      return 'finished';
    }
    return hit ? 'hit' : 'missed';
  }

  /**
   * Begin the next attempt. Called once the caller has actually replaced the
   * ball — the run does not know how long that takes, and the clock must not
   * start while the world is still holding a kickoff pose.
   */
  advance(): void {
    if (this.done || !this.resolved) return;
    this.index++;
    this.ticksLeft = this.limitTicks();
    this.resolved = false;
  }

  summary(): DrillSummary {
    return {
      drillId: this.spec.id,
      name: this.spec.name,
      hits: this.hitCount,
      // What was actually played, so an abandoned run does not claim ten.
      attempts: this.resolved || this.done ? this.index + 1 : this.index,
    };
  }
}

// ---------------------------------------------------------------------------
// Driving a run against a world
// ---------------------------------------------------------------------------

/** What a session needs of a world. `WorldAdapter` in src/main.ts satisfies it. */
export interface DrillHost extends DrillWorld {
  /**
   * Put the field back to a kickoff. Called for what a placement does not
   * cover — the boost pads, and the per-car impulse cooldowns that would
   * otherwise carry a suppressed hit into the next attempt.
   */
  resetForKickoff(): void;
}

/** What one stepped tick did. `placed` means the next shot is now on the pitch. */
export type DrillStep = 'live' | 'placed' | 'finished';

/**
 * A run, plus the two things a pure run cannot do: put a ball somewhere, and
 * hold it there for a beat.
 *
 * This lives here rather than in the session that owns the renderer so that
 * the whole loop can be tested against a real `World` in Node — which is the
 * only way to catch the failure this design exists to avoid, a world that
 * re-asserts a kickoff pose over the shot that was just placed.
 *
 * THE HOLD COMES FIRST, and while it runs the shot is re-asserted every tick.
 * That is what freezes the field: the world still steps, gravity still pulls,
 * and then the ball is put back. Idempotent by construction, so the beat costs
 * a copy of two vectors per tick and needs no frozen match phase of its own —
 * which matters, because a frozen phase is exactly what would overwrite the
 * placement (see `drillMatchConfig`).
 */
export class DrillSession {
  readonly run: DrillRun;
  private hold: number;
  private readonly holdTicks: number;

  constructor(spec: DrillSpec, ticksPerSecond: number, private readonly world: DrillHost) {
    this.run = new DrillRun(spec, ticksPerSecond);
    this.holdTicks = Math.max(1, Math.round(DRILL_RESET_SECONDS * ticksPerSecond));
    this.hold = this.holdTicks;
    applyDrillShot(world, this.run.shot());
  }

  /** True while the next shot is being held in place and the clock is stopped. */
  get holding(): boolean {
    return this.hold > 0;
  }

  /**
   * Fold in one tick, AFTER the world has been stepped.
   *
   * An own goal is not a hit, which is the caller's job to express: it passes
   * `ballScored` only for a goal its own team scored. A drill that
   * congratulated a player for putting it in their own net would be worse than
   * one with no test at all.
   */
  step(events: { ballScored: boolean; ballTouched: boolean }): DrillStep {
    if (this.hold > 0) {
      this.hold--;
      applyDrillShot(this.world, this.run.shot());
      return 'live';
    }
    if (this.run.finished) return 'finished';

    const outcome = this.run.observe(events);
    if (outcome === 'live') return 'live';
    if (outcome === 'finished') return 'finished';

    this.run.advance();
    this.world.resetForKickoff();
    applyDrillShot(this.world, this.run.shot());
    this.hold = this.holdTicks;
    return 'placed';
  }
}

/**
 * The line under a finished run.
 *
 * Three bands and no score out of a hundred: a drill is a thing to come back
 * to, and a number precise enough to have a bad day about is a number that
 * stops people coming back.
 */
export const drillVerdict = (summary: DrillSummary): string => {
  if (summary.attempts === 0) return 'Nothing attempted.';
  const rate = summary.hits / summary.attempts;
  if (rate >= 0.8) return 'That is the drill. Try the next one.';
  if (rate >= 0.4) return 'Getting there. Run it again.';
  return 'Slow down and take the first touch cleanly.';
};
